import React, { useMemo, useState } from 'react';

import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useGetIdentityActivityEvents } from '~/hooks/apis/users/useGetIdentityActivityEvents';

import { formatDateOrdinalWithClock } from '~/services/TimeService';
import type { AccountIdentityActivityLog, AccountIdentityAuthEvent } from '~/services/UsersService';

import {
  USER_DETAILS_GRID_CONFIG,
  USER_DETAILS_GRID_PAGE_SIZE,
  getErrorMessage,
  getUserDetailsGridHeight,
} from './utils';

const COLUMNS: Array<GridColDef<AccountIdentityAuthEvent>> = [
  {
    field: 'created_at',
    headerName: 'Sign-in time',
    minWidth: 200,
    flex: 1,
    valueGetter: (_value, row) => formatDateOrdinalWithClock(row.created_at),
  },
  { field: 'type', headerName: 'Event', minWidth: 130 },
  { field: 'response', headerName: 'Result', minWidth: 90 },
  { field: 'risk_level', headerName: 'Risk level', minWidth: 100 },
  { field: 'risk_decision', headerName: 'Risk decision', minWidth: 130 },
  {
    field: 'challenges',
    headerName: 'Challenge',
    minWidth: 200,
    flex: 1,
    valueGetter: (_value, row) => row.challenges.map(({ name, response }) => `${name}:${response}`).join(', '),
  },
  { field: 'device_name', headerName: 'Device', minWidth: 200, flex: 1 },
  { field: 'ip_address', headerName: 'IP address', minWidth: 140 },
  {
    field: 'city',
    headerName: 'Location (city)',
    minWidth: 160,
    valueGetter: (_value, row) => [row.city, row.country].filter(Boolean).join(', '),
  },
];

interface Props {
  accountId: string;
  log: AccountIdentityActivityLog;
}

export default function IdentitySignInHistoryTable({ accountId, log }: Props) {
  const [page, setPage] = useState(0);
  const { data, error, hasNextPage, fetchNextPage, isFetchingNextPage, isFetchNextPageError } =
    useGetIdentityActivityEvents(accountId, log);
  const rows = useMemo(() => data.pages.flatMap((logPage) => logPage.events), [data.pages]);

  return (
    <>
      <PageSubheader title="Cognito sign-in history" />
      <Stack direction={{ xs: 'column', lg: 'row' }} gap={3} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            User pool ID
          </Typography>
          <Typography>{log.user_pool_id ?? '-'}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Username
          </Typography>
          <Typography>{log.username}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Identity provider
          </Typography>
          <Typography>{log.identity_provider_name}</Typography>
        </Box>
      </Stack>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No sign-in activity has been recorded for this user.
        </Typography>
      ) : (
        <Box sx={{ height: getUserDetailsGridHeight(rows.length) }}>
          <DataGrid
            rows={rows}
            columns={COLUMNS}
            getRowId={(row) => row.id}
            {...USER_DETAILS_GRID_CONFIG}
            pageSizeOptions={[USER_DETAILS_GRID_PAGE_SIZE]}
            paginationModel={{ pageSize: USER_DETAILS_GRID_PAGE_SIZE, page }}
            onPaginationModelChange={({ page }) => setPage(page)}
            disableColumnMenu
            disableRowSelectionOnClick
            slots={{ pagination: GridPagination }}
          />
        </Box>
      )}
      {isFetchNextPageError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Unable to load more sign-in history: {getErrorMessage(error)}
        </Alert>
      )}
      {hasNextPage && (
        <Button sx={{ mt: 2 }} onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
          {isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </>
  );
}
